import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Profile } from '../onboarding';
import {
  getMeEnvelope,
  updateProfile,
  type MeEnvelope,
  type PublicPartner,
  type UpdateProfileResponse,
  type User,
} from './users';
/**
 * TownInsider `useUser()` hook — TanStack Query wrapper around
 * GET /v1/users/me.
 *
 * Named to mirror Clerk's `useUser` shape but explicitly TownInsider's:
 * returns the TownInsider user record (id, townSlug, joinedAt, email,
 * profile) rather than the Clerk user object (firstName, emailAddresses,
 * etc). If you need the Clerk identity, import `useUser as useClerkUser`
 * from `@clerk/clerk-react` — they're complementary.
 *
 * Query is enabled only while signed in. Stale time is generous (5 min)
 * — profile changes are infrequent and a brief stale read is harmless.
 *
 * Consumed by `MePage` from Batch 24c onward as the source of truth for
 * townSlug + profile (replacing the previous localStorage read). The
 * `personalPage` localStorage entry stays as a route-hint for
 * `RootRoute` but is no longer the live data source.
 *
 * Cache shape (Batch 7b-2b): /me's response widened to
 * `{ user, partner }` (Batch 6a added partner; Batch 7b-1 extended
 * partner with the persona projection). The TanStack cache stores the
 * FULL envelope (via `getMeEnvelope`); `useUser` keeps its existing
 * `User` return type via a `select` transform that picks `.user`.
 * Existing callers see no change. The partner sibling is surfaced by
 * `usePartner` below; both hooks share the SAME query key, so calling
 * both in the same render fires ONE network request (TanStack dedupes
 * by key) and both consumers update in the same render cycle when /me
 * resolves.
 *
 * Identity-scoped cache key (10 Jun 2026, structural fix for the
 * cross-account state-bleed bug surfaced during Harlyn pre-flight
 * testing):
 *
 *   queryKey: ['users', 'me', userId]
 *
 * Account A's /me envelope is cached under `['users', 'me', userIdA]`
 * and account B's under `['users', 'me', userIdB]`. The two are
 * different cache entries — there is no shared key from which a
 * cross-account read could occur. This makes the header/persona
 * crossover that occurred when one user signed out and another
 * signed in on the same browser structurally impossible; the
 * `clearAllAppStorage` + `queryClient.clear()` calls on sign-out
 * (SettingsPage handleSignOut, JoinPage handleSignOut) remain as
 * defence-in-depth hygiene — preventing unbounded cache growth on
 * shared devices — but are no longer load-bearing for isolation.
 *
 * `userId` comes from Clerk's `useAuth()` and is the Clerk user id
 * (e.g. `user_2abc...`) — stable for the lifetime of the account,
 * unique across all Clerk accounts in this instance. Coalesced to
 * `null` to keep the key shape stable while Clerk is still resolving
 * (the `enabled` gate below blocks the query firing on the null-key
 * variant, so no stray fetch occurs on the loading-state key).
 *
 * `enabled` is gated on BOTH `isSignedIn` and `userId` — belt and
 * braces: if Clerk ever flips `isSignedIn` true before `userId` is
 * populated (unlikely but defensible against SDK race conditions),
 * we still don't fire on the null-key variant.
 *
 * Invalidations via the partial key `['users', 'me']` (e.g. from
 * `JoinPage`'s `handleOpenMyPage` after lazy-first-touch attribution)
 * still match every user-scoped variant by prefix — TanStack Query's
 * `invalidateQueries` does partial key matching by default. No call
 * sites outside this file need to change.
 */
export function useUser() {
  const { isSignedIn, userId, getToken } = useAuth();
  return useQuery<MeEnvelope, Error, User>({
    queryKey: ['users', 'me', userId ?? null],
    queryFn: () => getMeEnvelope({ getToken }),
    select: (data) => data.user,
    enabled: !!isSignedIn && !!userId,
    staleTime: 5 * 60 * 1000,
  });
}
/**
 * Partner attribution for the current user — surfaces the public
 * partner projection (slug, name, kind, theme, persona) the user was
 * attributed to at sign-up. Returns null when no attribution applies
 * (bare URL sign-up, unrecognised ref, archived partner, town-
 * mismatched ref). Returns the PublicPartner shape when attributed,
 * including `partner.persona` for partner-curated personas
 * (Batch 7b-1 API projection + Batch 7b-2a app resolver).
 *
 * Implementation: shares the underlying `['users', 'me', userId]`
 * cache with `useUser` via TanStack Query's `select` transform. Both
 * hooks call `getMeEnvelope` and TanStack dedupes by key — calling
 * both in the same render fires ONE network request, and both
 * consumers update together when /me resolves (same query state
 * across instances). No race between user-resolved and
 * partner-resolved renders.
 *
 * Stale time, `enabled` gate, and the identity-scoped `userId` key
 * fragment all mirror `useUser` so the two hooks stay in lockstep —
 * if they ever diverge, dedup breaks and both hooks fire independent
 * fetches.
 */
export function usePartner() {
  const { isSignedIn, userId, getToken } = useAuth();
  return useQuery<MeEnvelope, Error, PublicPartner | null>({
    queryKey: ['users', 'me', userId ?? null],
    queryFn: () => getMeEnvelope({ getToken }),
    select: (data) => data.partner,
    enabled: !!isSignedIn && !!userId,
    staleTime: 5 * 60 * 1000,
  });
}
/**
 * Mutation hook for PATCH /v1/users/me/profile.
 *
 * Full replacement, not merge — the API treats the profile as an opaque
 * encrypted blob (pgcrypto pgp_sym_encrypt at rest, Article 9 layer).
 * The full Profile must be sent on every write.
 *
 * On success the returned user record is merged into the
 * `['users', 'me', userId]` cache via `setQueryData`, so the next
 * render of `useUser()` reflects the new profile without an extra
 * fetch. Faster UX, one fewer network round-trip than
 * invalidate-and-refetch.
 *
 * The `setQueryData` key carries the Clerk `userId` so it writes to
 * the same identity-scoped entry that `useUser` and `usePartner` read
 * — without this, the merged user would land at the legacy key
 * `['users', 'me']` and the read hooks (which look at
 * `['users', 'me', userId]`) would never see the new profile, so the
 * persona ack would fire while MePage still rendered against the
 * pre-save snapshot.
 *
 * Cache merge (Batch 7b-2b): the cache shape is the combined
 * `{ user, partner }` envelope (`MeEnvelope`). PATCH /me/profile's
 * response is `{ user }` only (no partner sibling — partner attribution
 * is captured at sign-up and surfaced by GET /me). The onSuccess
 * writer preserves the partner sibling from the previous cache value
 * while substituting the freshly-returned user. usePartner consumers
 * don't see their data evicted on profile saves.
 *
 * Caller pattern (onboarding chat in MePage final step):
 *
 *   const { mutateAsync: saveProfile } = useUpdateProfile();
 *   try {
 *     await saveProfile(profile);
 *     // show persona ack
 *   } catch {
 *     // show persona soft error; user can re-tap to retry
 *   }
 *
 * Awaiting is intentional. The persona ack ("Lovely. 11–18 May, a
 * couple, with a dog…") must only fire once the write succeeds — if
 * the API errors and the user reloads later, they should re-do
 * onboarding rather than see an ack for a profile that was never
 * persisted.
 */
export function useUpdateProfile() {
  const { userId, getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation<UpdateProfileResponse, Error, Profile>({
    mutationFn: (profile) => updateProfile({ profile, getToken }),
    onSuccess: (updatedUser) => {
      queryClient.setQueryData<MeEnvelope>(
        ['users', 'me', userId ?? null],
        (prev) => ({
          user: updatedUser,
          partner: prev?.partner ?? null,
        }),
      );
    },
  });
}
